// MinimalShell — the chrome for the pages a login may use OUTSIDE the builder
// (docs/follow-ups-plan.md §3, §5): Follow-ups, open to every signed-in
// welcomehps.com address, and the Accommodations console, open to the RRA
// roster whatever the login's roles.
//
// The builder may be admin-only, so this shell carries no way into it: the
// monogram is a mark, not a link; there is no Tools menu, no search. What is
// left is the same 56px topbar the builder uses (so the two read as one
// product), the page's name, the non-builder pages this login may use (only
// when there is more than one — see minimalNavFor), who is signed in, and Sign
// out. It mounts its own <Toaster/> because AppShell's is not on these pages.

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/Toast';
import type { MinimalNavLink } from '@/components/RequireBuilder';
import { OPAQUE_ID_RE } from '@/lib/noIds';
// The tight crop: the shared asset's artwork fills a third of its canvas, so at a
// 32 px box the mark itself was a 12 px speck.
import monogramUrl from '@/design-system/assets/brand/boxed-monogram-light-tight.svg';

export interface MinimalShellProps {
  /** The page's name, shown beside the mark. */
  title: string;
  /** Links to the other non-builder pages this login may use; never a builder page. */
  nav?: MinimalNavLink[] | undefined;
  children: ReactNode;
}

export function MinimalShell({ title, nav, children }: MinimalShellProps): React.JSX.Element {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  // Names and labels only: a token whose name is an opaque id shows the email instead.
  const words = (v: string | undefined | null): string | null => (v && !OPAQUE_ID_RE.test(v) ? v : null);
  const displayName = words(user?.name) ?? words(user?.email) ?? words(user?.preferredUsername) ?? null;

  return (
    <div className="app-shell" style={{ gridTemplateColumns: '1fr', gridTemplateRows: '56px 1fr' }}>
      <header className="app-topbar" style={{ height: 56, minHeight: 56 }}>
        {/* Same markup as AppShell's brand mark (an inline-flex box around the
            img). The SVG has only a viewBox, and as a bare flex child it was
            once drawn at its full canvas size over the page; the inline size
            wins over any stylesheet rule. 36 px: a mark with presence beside
            the 16 px title in a 56 px bar. */}
        <span style={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto' }}>
          <img
            src={monogramUrl}
            alt="WHP"
            height={36}
            width={36}
            style={{ display: 'block', height: 36, width: 36, maxWidth: 'none' }}
          />
        </span>
        <h1 className="app-brand" style={{ margin: 0, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
          {title}
        </h1>
        {nav && nav.length > 0 && (
          <nav className="cluster" aria-label="Pages" style={{ gap: 'var(--space-1)' }}>
            {nav.map(({ label, path }) => (
              <Link
                key={path}
                to={path}
                className="app-nav-link"
                aria-current={pathname === path || pathname.startsWith(`${path}/`) ? 'page' : undefined}
              >
                {label}
              </Link>
            ))}
          </nav>
        )}
        <span className="app-topbar-fill" />
        {user && (
          <div className="cluster" style={{ gap: 'var(--space-3)' }}>
            {displayName && (
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                {displayName}
              </span>
            )}
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              onClick={() => void logout()}
            >
              Sign out
            </button>
          </div>
        )}
      </header>

      <main className="app-main">{children}</main>
      <Toaster />
    </div>
  );
}
